import React, { useRef, useState } from "react";
import { api, API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, fmtDate, titleCase } from "@/lib/format";
import { FormDialog } from "@/components/EntityForm";
import { DataTable } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Upload, Check, X, RotateCcw, Pencil } from "lucide-react";

export const run = async (fn, ok) => {
  try { await fn(); if (ok) toast.success(ok); return true; }
  catch (ex) { toast.error(apiError(ex.response?.data?.detail)); return false; }
};

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-border last:border-0 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right" data-testid={testId}>{value ?? "—"}</span>
  </div>
);

export function Card({ title, actions, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="font-bold">{title}</h3>
        <div className="flex gap-2 flex-wrap">{actions}</div>
      </div>
      {children}
    </div>
  );
}

export function KycPanel({ agent, reload }) {
  const { hasPerm } = useAuth();
  const set = (kyc_status, extra = {}) => run(() => api.put(`/agents/${agent.id}/kyc`, { kyc_status, ...extra }), "KYC updated").then(reload);
  return (
    <Card title="KYC Verification" actions={hasPerm("agents:verify") && <>
      <Button size="sm" variant="outline" onClick={() => set("under_review")} data-testid="kyc-review-btn">Mark Under Review</Button>
      <Button size="sm" onClick={() => set("verified")} data-testid="kyc-verify-btn"><Check className="h-4 w-4 mr-1" />Verify</Button>
      <FormDialog title="Reject KYC" fields={[{ name: "rejection_reason", label: "Rejection reason", required: true, type: "textarea", full: true }]}
        onSubmit={async (v) => { const r = await api.put(`/agents/${agent.id}/kyc`, { kyc_status: "rejected", ...v }); reload(); return r; }}
        submitLabel="Reject" trigger={<Button size="sm" variant="destructive" data-testid="kyc-reject-btn"><X className="h-4 w-4 mr-1" />Reject</Button>} />
    </>}>
      <Row label="Status" value={<StatusBadge value={agent.kyc_status} />} testId="kyc-status" />
      <Row label="PAN" value={agent.can_view_sensitive ? agent.pan : agent.pan_masked} testId="kyc-pan" />
      <Row label="Aadhaar" value={agent.aadhaar_masked} testId="kyc-aadhaar" />
      <Row label="Rejection reason" value={agent.kyc_rejection_reason} />
      <Row label="Reviewed by" value={agent.kyc_reviewed_by ? `${agent.kyc_reviewed_by} · ${fmtDate(agent.kyc_reviewed_at)}` : null} />
    </Card>
  );
}

export function GstPanel({ agent, reload }) {
  const { hasPerm } = useAuth();
  const fields = [
    { name: "gst_registered", label: "GST Registered?", type: "select", options: [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }] },
    { name: "gstin", label: "GSTIN" },
    { name: "gst_business_name", label: "Legal Business Name" },
    { name: "gst_trade_name", label: "Trade Name" },
    { name: "gst_registration_type", label: "Registration Type", type: "select", options: ["Regular", "Composition", "Other"] },
    { name: "gst_state", label: "GST State" },
    { name: "gst_effective_date", label: "Effective Date", type: "date" },
    { name: "gst_expiry_date", label: "Expiry / Cancellation Date", type: "date" },
    ...(hasPerm("agents:verify") ? [{ name: "gst_status", label: "Manual Verification", type: "select", options: ["pending", "verified", "rejected"].map((v) => ({ value: v, label: titleCase(v) })) }] : []),
    { name: "gst_address", label: "Registered Address", type: "textarea", full: true },
  ];
  return (
    <Card title="GST Registration" actions={hasPerm("agents:edit") && (
      <FormDialog title="Edit GST" fields={fields} initial={{ ...agent, gst_status: agent.gst_status === "not_applicable" ? "" : agent.gst_status }} submitLabel="Save GST"
        onSubmit={async (v) => { const b = { ...v }; if (!b.gst_status || b.gst_status === agent.gst_status) delete b.gst_status; await api.put(`/agents/${agent.id}/gst`, b); reload(); }}
        trigger={<Button size="sm" variant="outline" data-testid="gst-edit-btn"><Pencil className="h-4 w-4 mr-1" />Edit GST</Button>} />
    )}>
      <Row label="Registered" value={titleCase(agent.gst_registered || "no")} />
      <Row label="Status" value={<StatusBadge value={agent.gst_status} />} testId="gst-status" />
      <Row label="GSTIN" value={agent.gstin} testId="gst-gstin" />
      <Row label="Format check" value={agent.gstin ? (agent.gst_format_valid ? "Format OK (not an official check)" : "Invalid format") : null} />
      <Row label="Business / Trade name" value={[agent.gst_business_name, agent.gst_trade_name].filter(Boolean).join(" / ") || null} />
      <Row label="Verified by" value={agent.gst_verified_by ? `${agent.gst_verified_by} · ${fmtDate(agent.gst_verification_date)}` : null} />
    </Card>
  );
}

export function BankPanel({ agent, reload }) {
  const { hasPerm } = useAuth();
  const fields = [
    { name: "bank_account_holder", label: "Account Holder Name" },
    { name: "bank_name", label: "Bank Name" },
    { name: "bank_account_number", label: "Account Number (leave blank to keep)" },
    { name: "bank_ifsc", label: "IFSC" },
    ...(hasPerm("agents:verify") ? [{ name: "bank_verification_status", label: "Verification", type: "select", options: ["pending", "verified", "rejected"].map((v) => ({ value: v, label: titleCase(v) })) }] : []),
  ];
  const save = async (v) => {
    const body = { ...v };
    if (!body.bank_account_number) delete body.bank_account_number;
    if (body.bank_verification_status === agent.bank_verification_status) delete body.bank_verification_status;
    await api.put(`/agents/${agent.id}/bank`, body); reload();
  };
  return (
    <Card title="Bank Details" actions={hasPerm("agents:edit") && (
      <FormDialog title="Edit Bank Details" fields={fields} submitLabel="Save Bank"
        initial={{ bank_account_holder: agent.bank_account_holder, bank_name: agent.bank_name, bank_ifsc: agent.bank_ifsc, bank_verification_status: agent.bank_verification_status }}
        onSubmit={save} trigger={<Button size="sm" variant="outline" data-testid="bank-edit-btn"><Pencil className="h-4 w-4 mr-1" />Edit Bank</Button>} />
    )}>
      <Row label="Status" value={<StatusBadge value={agent.bank_verification_status} />} testId="bank-status" />
      <Row label="Holder" value={agent.bank_account_holder} />
      <Row label="Bank" value={agent.bank_name} />
      <Row label="Account" value={agent.can_view_sensitive && agent.bank_account_number ? agent.bank_account_number : agent.bank_account_masked} testId="bank-account" />
      <Row label="IFSC" value={agent.bank_ifsc} />
      <p className="text-xs text-muted-foreground mt-2">Changing the account number or IFSC sets verification back to Pending.</p>
    </Card>
  );
}

const DOC_TYPES = ["PAN Card", "Identity Proof", "Address Proof", "Photograph", "Bank Proof", "Business Registration Certificate", "GST Certificate", "Agreement", "Other"];

export function DocsPanel({ agent, docs, profile, reload }) {
  const { hasPerm } = useAuth();
  const [type, setType] = useState("PAN Card");
  const fileRef = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("doc_type", type);
    await run(() => api.post(`/agents/${agent.id}/documents`, fd), "Document uploaded");
    e.target.value = ""; reload();
  };
  const review = (d, s) => run(() => api.put(`/agents/documents/${d.id}/review`, { verification_status: s }), "Review saved").then(reload);
  const cols = [
    { key: "doc_type", header: "Type", render: (d) => <span className="font-medium">{d.doc_type}</span> },
    { key: "original_filename", header: "File", render: (d) => <span className="text-xs">{d.original_filename}</span> },
    { key: "created_at", header: "Uploaded", render: (d) => fmtDate(d.created_at) },
    { key: "verification_status", header: "Review", render: (d) => <div><StatusBadge value={d.verification_status} />{d.reviewer && <div className="text-[11px] text-muted-foreground mt-0.5">{d.reviewer}</div>}</div> },
    { key: "a", header: "", render: (d) => (
      <div className="flex gap-1 justify-end">
        <Button size="icon" variant="ghost" className="h-8 w-8" title="Download" data-testid="agent-doc-download-btn" onClick={() => window.open(`${API}/agents/documents/${d.id}/download`, "_blank")}><Download className="h-4 w-4" /></Button>
        {hasPerm("agents:verify") && <>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" title="Approve" data-testid="agent-doc-approve-btn" onClick={() => review(d, "approved")}><Check className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-600" title="Ask for new copy" data-testid="agent-doc-request-btn" onClick={() => review(d, "update_requested")}><RotateCcw className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" title="Reject" data-testid="agent-doc-reject-btn" onClick={() => review(d, "rejected")}><X className="h-4 w-4" /></Button>
        </>}
      </div>) },
  ];
  return (
    <Card title="KYC & Business Documents" actions={hasPerm("agents:edit") && <>
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="w-56 h-9" data-testid="agent-doc-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
      </Select>
      <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="agent-doc-file-input" />
      <Button size="sm" onClick={() => fileRef.current?.click()} data-testid="agent-doc-upload-btn"><Upload className="h-4 w-4 mr-1" />Upload</Button>
    </>}>
      {profile?.missing_documents?.length > 0 && (
        <div className="mb-3 text-sm rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 px-3 py-2" data-testid="agent-missing-docs">
          Missing: {profile.missing_documents.join(", ")}
        </div>
      )}
      <DataTable columns={cols} rows={docs} rowTestId="agent-doc-row" emptyText="No documents uploaded yet" />
    </Card>
  );
}
